import { type TriggerProps } from '@atlaskit/popup';
import { useCommentActionsContext } from '@databases/comments/hooks/useCommentContext';
import SelectValueMenu from '@databases/context-menu/select-value-menu';
import { createContextualMenuContext } from '@databases/context-menu/select-value-menu/utils/create-contextual-menu-context';
import {
  createContextualMenuItems,
  type MenuItemMap,
} from '@databases/context-menu/select-value-menu/utils/create-contextual-menu-items';
import { useContextualMenu } from '@databases/contextual-menu';
import { useDialog } from '@databases/dialog';
import FieldNameWithActions from '@databases/field-name/FieldNameWithActions';
import usePanel from '@databases/panel/usePanel';
import { PluginKey } from '@databases/plugins';
import { isPluginEnabled } from '@databases/plugins/helper';
import { defaultStoreUtils } from '@databases/redux';
import {
  selectValueRange,
  selectValueSingle,
} from '@databases/redux/cellSelections/actions';
import {
  selectIsValueSelected,
  selectSelectedEntireEntryIds,
} from '@databases/redux/cellSelections/selectors';
import { getDatabaseFieldRequired } from '@databases/utils/database/utils';
import { useIsFeatureEnabled } from '@databases/utils/hooks/useIsFeatureEnabled';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import DatabaseValueInlineEdit from '@databases/value/DatabaseValueInlineEdit';
import useView from '@databases/views/useView';
import React, {
  type FunctionComponent,
  type MutableRefObject,
  useCallback,
  useState,
} from 'react';
import { useIntl } from 'react-intl-next';

export interface DatabaseEntryRowProps {
  contentId: string;
  entryId: string;
  fieldId: string;
  isContentEditable: boolean;
  canEditContent: boolean;
  canEditFields: boolean;
  editingIds?: MutableRefObject<Set<string>>;
  autoFocus?: boolean;
}

/**
 * Renders a table that shows all the field names and values of a database entry. The field names and values can be edited
 * using inline edit fields, and new fields can be added. Any edits will cause a database update to be drafted automatically.
 */
const DatabaseEntryRow: FunctionComponent<DatabaseEntryRowProps> = ({
  contentId,
  entryId,
  fieldId,
  isContentEditable,
  canEditContent,
  editingIds,
  autoFocus,
  canEditFields,
}) => {
  const dispatch = defaultStoreUtils.useDispatch();
  const { view } = useView();
  const [isHover, setIsHover] = useState(false);

  const [field, isSelected] = defaultStoreUtils.useEqualSelector((state) => {
    const database = selectDatabaseByContentIdRequired(state, {
      contentId,
    });
    const entriesSelected = selectSelectedEntireEntryIds(state, {
      contentId,
    });
    const isSelected = entriesSelected.length
      ? undefined
      : selectIsValueSelected(state, { entryId, fieldId });
    return [getDatabaseFieldRequired(database, fieldId), isSelected] as const;
  });

  const initialIsEditing = editingIds?.current.has(field.id);
  if (initialIsEditing) {
    // Since we use editingIds only to set the initialIsEditing prop, it is enough to set it once during the initial
    // render of the inline edit field. We don't want the field to steal the focus again when it is hidden and then
    // shown again because of a filter change. Because of this, we remove the entry from the list once it has been
    // focused for the first time.
    editingIds?.current.delete(field.id);
  }

  const { toggleOpenMenu } = useContextualMenu({
    type: 'value',
    entryId,
    fieldId,
    panel: true,
  });

  const onIsSelectedChange = useCallback(
    (options?: { isMultiSelectOn?: boolean }) => {
      if (isContentEditable) {
        if (options?.isMultiSelectOn) {
          dispatch(
            selectValueRange({
              contentId,
              entryId: entryId,
              fieldId: fieldId,
            })
          );
        } else {
          dispatch(
            selectValueSingle({
              contentId,
              entryId: entryId,
              fieldId: fieldId,
            })
          );
        }
      }
    },
    [isContentEditable, dispatch, contentId, entryId, fieldId]
  );

  const isContextMenuEnabled = isPluginEnabled(PluginKey.CONTEXT_MENU);

  const [menuItems, setMenuItems] = useState<MenuItemMap>({});
  const [canInsertContent, setCanInsertContent] = useState(false);
  const panels = usePanel();
  const dialogs = useDialog();
  const { setOpenThread } = useCommentActionsContext();
  const { formatMessage } = useIntl();
  const isDBAiFirstCreationEnabled = useIsFeatureEnabled('dbAiFirstCreation');

  const onCloseMenu = useCallback(() => {
    setMenuItems({});
  }, [setMenuItems]);

  const handleOnContextMenu = useCallback(
    (e: React.MouseEvent) => {
      onIsSelectedChange();

      if (isContentEditable) {
        e.preventDefault();
        toggleOpenMenu();

        const context = createContextualMenuContext({
          canEditContent,
          canEditFields,
          comments: {
            setOpenThread,
          },
          currentViewId: view.id,
          contentId,
          dialogs,
          entryId,
          fieldId,
          formatMessage,
          isDBAiFirstCreationEnabled,
          panels,
        });
        const computedMenuItems = createContextualMenuItems(context);

        setMenuItems(computedMenuItems);
        setCanInsertContent(context.capabilities.canInsertContent);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      onIsSelectedChange,
      isContentEditable,
      toggleOpenMenu,
      canEditContent,
      canEditFields,
      contentId,
      dialogs,
      entryId,
      fieldId,
      formatMessage,
      isDBAiFirstCreationEnabled,
      panels,
      view,
    ]
  );

  const triggerComponent = useCallback(
    (triggerProps: TriggerProps) => (
      <td
        onContextMenu={isContextMenuEnabled ? handleOnContextMenu : undefined}
        ref={triggerProps?.ref}
        onMouseEnter={() => setIsHover(true)}
        onMouseLeave={() => setIsHover(false)}
      >
        <DatabaseValueInlineEdit
          contentId={contentId}
          fieldId={fieldId}
          entryId={entryId}
          initialIsEditing={initialIsEditing || autoFocus}
          isReadonly={!canEditContent}
          isContentEditable={isContentEditable}
          isSelected={isSelected}
          onIsSelectedChange={onIsSelectedChange}
          enableSelection
          panel={true}
          isHoverCell={isHover}
          setIsHoverCell={setIsHover}
          {...triggerProps}
        />
      </td>
    ),
    [
      autoFocus,
      canEditContent,
      contentId,
      entryId,
      fieldId,
      handleOnContextMenu,
      initialIsEditing,
      isContentEditable,
      isHover,
      isSelected,
      onIsSelectedChange,
      isContextMenuEnabled,
    ]
  );

  return (
    <tr onContextMenu={(e) => e.preventDefault()}>
      <th id={fieldId}>
        <FieldNameWithActions
          contentId={contentId}
          fieldId={fieldId}
          isReadonly={!canEditFields}
          isSelected={false}
        />
      </th>
      <SelectValueMenu
        Trigger={triggerComponent}
        contentId={contentId}
        currentViewId={view.id}
        canEditContent={canEditContent}
        entryId={entryId}
        fieldId={fieldId}
        placement={'auto-end'}
        panel={true}
        menuItems={menuItems}
        canInsertContent={canInsertContent}
        onCloseMenu={onCloseMenu}
      />
    </tr>
  );
};

export default DatabaseEntryRow;
